import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { ExperienceTrackerContext } from '@atlassian/experience-tracker/component/ExperienceTrackerContext';
import type { EditPageProps } from '../edit-page';
import { useExperienceTracker } from '../experience-tracker/useExperienceTracker';
import type { Modifiers } from '../navigation-policy';
import { EMBEDDED_CONFLUENCE_MODE } from '../page-common/EmbeddedConfluenceMode';
import type { ViewPageProps } from '../view-page';
import { type PageProps } from './PageProps';
import { parsePageProps } from './parsePageProps';
import { useIntl } from 'react-intl';
const EDIT_PAGE_CLOSE = 'edit-page/close';
const EDIT_PAGE_PUBLISH = 'edit-page/publish';

export type Props = PageProps & {
	editComponent: (props: EditPageProps) => React.ReactElement;
	viewComponent: (props: ViewPageProps) => React.ReactElement;
};

export const Page = (props: Props): React.JSX.Element => {
	const { viewComponent: ViewComponent, editComponent: EditComponent } = props;
	const {
		mode,
		allowedFeatures,
		draftShareId,
		navigationPolicy,
		spaceKey,
		showFooter,
		...passThroughProps
	} = parsePageProps(props);
	const { onModeChange } = props;
	const [currentMode, setCurrentMode] = useState(mode);

	useEffect(() => {
		setCurrentMode(mode);
	}, [mode]);

	const [currentSpaceKey, setCurrentSpaceKey] = useState(spaceKey);

	const switchToViewPage = useCallback(() => {
		setCurrentMode(EMBEDDED_CONFLUENCE_MODE.VIEW_MODE);
	}, [setCurrentMode]);

	const pageNavigationPolicy = useMemo(
		() => ({
			navigate(
				url: string,
				modifiers: Modifiers,
				defaultNavigate: (url: string, modifiers: Modifiers) => void,
			) {
				/**
				 * Below is for switching from View to Edit and we assume parent product doesn't have this situation in their navigationPolicy.
				 */
				if (
					currentMode === EMBEDDED_CONFLUENCE_MODE.VIEW_MODE &&
					(modifiers.routeName === 'EDIT_PAGE_EMBED' ||
						(url?.includes('/edit-embed/') &&
							passThroughProps.contentId === modifiers.contentId)) &&
					passThroughProps.contentId === modifiers.contentId
				) {
					setCurrentMode(EMBEDDED_CONFLUENCE_MODE.EDIT_MODE);
					onModeChange?.(EMBEDDED_CONFLUENCE_MODE.EDIT_MODE);
					return;
				}
				return navigationPolicy?.navigate
					? navigationPolicy.navigate(url, modifiers, defaultNavigate)
					: defaultNavigate(url, modifiers);
			},
			shimUrl: navigationPolicy?.shimUrl,
			domainNamesToReplace: navigationPolicy?.domainNamesToReplace,
			spaceKeyFilter: navigationPolicy?.spaceKeyFilter,
		}),
		[currentMode, passThroughProps.contentId, navigationPolicy, onModeChange],
	);

	const experienceTracker = useExperienceTracker();

	useEffect(
		() =>
			experienceTracker.subscribe((event) => {
				const { name, action, attributes } = event;

				/**
				 * Using experienceTracker, we can switch from Edit to View page.
				 * 1. When the user clicks the Publish button and task success, it can switch to the View page.
				 *    a. If the `publish-modal` is added to the allowedFeatures, the Publish Modal may be used to
				 *          change the spaceKey during publish.
				 *          In that case, a new spaceKey will be added to the success event and tracked here.
				 * 2. If the page is not unpublished draft, when the user clicks Close button and task success, it can switch to the View page.
				 */
				const isClosingPublishedEditPage = !draftShareId && name === EDIT_PAGE_CLOSE;
				const isPublishingDraft = name === EDIT_PAGE_PUBLISH;
				if (action === 'taskSuccess' && (isClosingPublishedEditPage || isPublishingDraft)) {
					if (allowedFeatures.edit?.includes('publish-modal')) {
						type eventAttributes = { newSpaceKey?: string };
						const newSpaceKey = (attributes as eventAttributes)?.newSpaceKey;
						setCurrentSpaceKey(newSpaceKey || spaceKey);
					}
					switchToViewPage();
				}
			}),
		[draftShareId, experienceTracker, switchToViewPage, allowedFeatures.edit, spaceKey],
	);

	const { locale } = useIntl();

	return (
		<ExperienceTrackerContext.Provider value={experienceTracker}>
			{currentMode === EMBEDDED_CONFLUENCE_MODE.VIEW_MODE ? (
				<ViewComponent
					navigationPolicy={pageNavigationPolicy}
					allowedFeatures={allowedFeatures.view}
					locale={locale}
					spaceKey={currentSpaceKey}
					showFooter={showFooter}
					{...passThroughProps}
				/>
			) : currentMode === EMBEDDED_CONFLUENCE_MODE.EDIT_MODE ? (
				<EditComponent
					navigationPolicy={pageNavigationPolicy}
					allowedFeatures={allowedFeatures.edit}
					draftShareId={draftShareId}
					locale={locale}
					spaceKey={currentSpaceKey}
					{...passThroughProps}
				/>
			) : null}
		</ExperienceTrackerContext.Provider>
	);
};
